import { PriceDisplay } from "@/components/ui/price-display";
import type { PricingMode } from "@/gql/graphql";
import { getPriceDisplayText } from "@/lib/utils/pricing-utils";
import { PricingModeBadge } from "./PricingModeBadge";

interface ServicePriceDisplayProps {
  mode: PricingMode;
  basePrice?: number;
  minPrice?: number;
  maxPrice?: number;
  currency?: string;
  showBadge?: boolean;
  showOnlyPrice?: boolean;
  className?: string;
}

export function ServicePriceDisplay({
  mode,
  basePrice,
  minPrice,
  maxPrice,
  currency = "USD",
  showBadge = true,
  showOnlyPrice = false,
  className = "",
}: ServicePriceDisplayProps) {
  const priceText = getPriceDisplayText(mode, basePrice, minPrice, maxPrice, currency);

  if (showOnlyPrice) {
    return <span className={`font-medium ${className}`}>{priceText}</span>;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="font-medium">{priceText}</span>
      {showBadge && mode === "MATRIX_PRICE" && <PricingModeBadge mode={mode} size="sm" />}
    </div>
  );
}

interface ServicePriceDetailsProps {
  mode: PricingMode;
  basePrice?: number;
  optionsTotal?: number;
  finalPrice?: number;
  currency?: string;
  showBreakdown?: boolean;
}

export function ServicePriceDetails({
  mode,
  basePrice,
  optionsTotal = 0,
  finalPrice,
  currency = "USD",
  showBreakdown = false,
}: ServicePriceDetailsProps) {
  if (mode === "NO_PRICE") {
    return <div className="text-sm text-gray-600">Price will be quoted upon consultation</div>;
  }

  const displayPrice = finalPrice || basePrice || 0;

  return (
    <div className="space-y-1">
      <div className="font-medium text-lg">
        <PriceDisplay amount={displayPrice} currency={currency} />
      </div>

      {showBreakdown && mode === "MATRIX_PRICE" && basePrice && optionsTotal > 0 && (
        <div className="text-sm text-gray-600 space-y-1">
          <div className="flex justify-between">
            <span>Base price:</span>
            <span>
              <PriceDisplay amount={basePrice} currency={currency} />
            </span>
          </div>
          <div className="flex justify-between">
            <span>Options:</span>
            <span>
              +<PriceDisplay amount={optionsTotal} currency={currency} />
            </span>
          </div>
          <div className="border-t pt-1 flex justify-between font-medium">
            <span>Total:</span>
            <span>
              <PriceDisplay amount={displayPrice} currency={currency} />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
